<template>
  <div class="sx">
    <header class="sx-head">
      <span class="sx-eyebrow">Paramètres</span>
      <h1 class="sx-title">Statuts des colis</h1>
      <p class="sx-sub">Nom et couleur de chaque étape du colis, affichés dans les listes, les suivis et les tableaux de bord.</p>
    </header>

    <SettingsNav />

    <div class="sx-grid">
      <!-- ── Add ─────────────────────────────────────────────────── -->
      <form class="sx-card sx-form-card" novalidate @submit.prevent="submitForm">
        <header class="sx-card-head">
          <span class="sx-card-icon"><i class="ph ph-plus-circle" aria-hidden="true"></i></span>
          <div>
            <h2>Nouveau statut</h2>
            <p>Le code est l'identifiant technique, il ne pourra plus être modifié.</p>
          </div>
        </header>

        <div class="sx-field">
          <label for="st-new-name">Nom <span class="sx-req" aria-hidden="true">*</span></label>
          <input
            id="st-new-name"
            v-model="newStatut.name"
            type="text"
            class="form-control"
            :class="{ 'is-invalid': errors.name }"
            maxlength="100"
            placeholder="Ex. Retour en cours"
            @input="onNameInput"
          />
          <span v-if="errors.name" class="sx-err">{{ errors.name }}</span>
        </div>

        <div class="sx-field">
          <label for="st-new-code">Code <span class="sx-req" aria-hidden="true">*</span></label>
          <input
            id="st-new-code"
            v-model="newStatut.code"
            type="text"
            class="form-control sx-mono"
            :class="{ 'is-invalid': errors.code }"
            maxlength="50"
            placeholder="RETOUR_EN_COURS"
            @input="onCodeInput"
          />
          <span v-if="errors.code" class="sx-err">{{ errors.code }}</span>
          <span v-else class="sx-help">MAJUSCULES, chiffres et _ uniquement. Généré depuis le nom.</span>
        </div>

        <div class="sx-field">
          <label for="st-new-color">Couleur</label>
          <div class="sx-color">
            <input id="st-new-color" v-model="newStatut.code_color" type="color" aria-label="Choisir une couleur" />
            <input v-model="newStatut.code_color" type="text" class="form-control" maxlength="7" aria-label="Code couleur" />
          </div>
          <div class="sx-swatches" role="group" aria-label="Couleurs suggérées">
            <button
              v-for="c in swatches"
              :key="c"
              type="button"
              :style="{ background: c }"
              :class="{ 'is-active': newStatut.code_color.toLowerCase() === c.toLowerCase() }"
              :aria-label="`Couleur ${c}`"
              @click="newStatut.code_color = c"
            ></button>
          </div>
          <span v-if="errors.code_color" class="sx-err">{{ errors.code_color }}</span>
        </div>

        <div class="sx-field">
          <label>Aperçu</label>
          <span class="st-pill" :style="pillStyle(newStatut.code_color)">{{ newStatut.name || "Nom du statut" }}</span>
        </div>

        <button type="submit" class="btn btn-primary sx-submit" :disabled="saving">
          <span v-if="saving" class="spinner-border spinner-border-sm" role="status"></span>
          Ajouter le statut
        </button>
      </form>

      <!-- ── List ────────────────────────────────────────────────── -->
      <section class="sx-card" aria-labelledby="st-list-title">
        <div class="sx-list-head">
          <h2 id="st-list-title">Statuts <span class="sx-count">{{ statuts.length }}</span></h2>
          <div class="sx-search">
            <i class="ph ph-magnifying-glass" aria-hidden="true"></i>
            <input v-model="search" type="search" placeholder="Rechercher un statut…" aria-label="Rechercher un statut" />
          </div>
        </div>

        <div v-if="loading">
          <span v-for="n in 5" :key="n" class="sx-skel"></span>
        </div>
        <div v-else-if="loadError" class="sx-load-error" role="alert">
          <i class="ph ph-warning-circle" aria-hidden="true"></i>
          Impossible de charger les statuts.
          <button type="button" class="btn btn-sm btn-light" @click="fetchStatus">Réessayer</button>
        </div>
        <div v-else-if="!filtered.length" class="sx-empty">
          <i class="ph ph-flag" aria-hidden="true"></i>
          <p>{{ search ? "Aucun statut ne correspond à votre recherche." : "Aucun statut pour le moment." }}</p>
        </div>
        <ul v-else class="sx-rows">
          <li v-for="item in filtered" :key="item.id" class="sx-row" :class="{ 'is-new': item.id === lastAddedId }">
            <label class="st-dot" :title="`Changer la couleur de ${item.name}`">
              <input
                type="color"
                :value="item.code_color || '#ffffff'"
                :aria-label="`Couleur de ${item.name}`"
                @input="onColorChange(item.id, $event.target.value)"
              />
              <span :style="{ background: item.code_color || '#ffffff' }"></span>
            </label>
            <div class="sx-main">
              <strong>{{ item.name }}</strong>
              <small><span class="sx-code">{{ item.code }}</span></small>
            </div>
            <span class="st-pill" :style="pillStyle(item.code_color)">{{ item.name }}</span>
            <div class="sx-actions">
              <button
                v-if="isColorDirty(item)"
                type="button"
                class="btn btn-sm btn-primary"
                :disabled="savingId === item.id"
                @click="updateColor(item)"
              >
                <span v-if="savingId === item.id" class="spinner-border spinner-border-sm" role="status"></span>
                Enregistrer
              </button>
              <button type="button" class="sx-icon-btn" :aria-label="`Modifier ${item.name}`" title="Modifier" @click="openEdit(item)">
                <i class="ph ph-pencil-simple" aria-hidden="true"></i>
              </button>
            </div>
          </li>
        </ul>
      </section>
    </div>

    <!-- ── Edit modal ────────────────────────────────────────────── -->
    <div ref="editModalEl" class="modal fade" tabindex="-1" aria-labelledby="st-edit-title" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <form class="modal-content" novalidate @submit.prevent="saveEdit">
          <div class="modal-header">
            <h2 id="st-edit-title" class="modal-title">Modifier le statut</h2>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Fermer"></button>
          </div>
          <div class="modal-body">
            <div class="sx-field">
              <label for="st-edit-name">Nom <span class="sx-req" aria-hidden="true">*</span></label>
              <input id="st-edit-name" v-model="edit.name" type="text" class="form-control" :class="{ 'is-invalid': editErrors.name }" maxlength="100" />
              <span v-if="editErrors.name" class="sx-err">{{ editErrors.name }}</span>
            </div>
            <div class="sx-field">
              <label>Code</label>
              <span class="sx-code">{{ edit.code }}</span>
              <span class="sx-help">Le code est une clé système et ne peut pas être modifié.</span>
            </div>
            <div class="sx-field">
              <label for="st-edit-color">Couleur</label>
              <div class="sx-color">
                <input id="st-edit-color" v-model="edit.code_color" type="color" aria-label="Choisir une couleur" />
                <input v-model="edit.code_color" type="text" class="form-control" maxlength="7" aria-label="Code couleur" />
              </div>
              <span v-if="editErrors.code_color" class="sx-err">{{ editErrors.code_color }}</span>
            </div>
            <span class="st-pill" :style="pillStyle(edit.code_color)">{{ edit.name || "Nom du statut" }}</span>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">Annuler</button>
            <button type="submit" class="btn btn-primary" :disabled="savingEdit">
              <span v-if="savingEdit" class="spinner-border spinner-border-sm" role="status"></span>
              Enregistrer
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import SettingsNav from "./SettingsNav.vue";
import { computed, defineComponent, onBeforeUnmount, onMounted, reactive, ref } from "vue";
import { Modal } from "bootstrap";
import api from "@/utils/axios";
import { useAppToast } from "@/composables/useAppToast";

interface Statut {
  id: number;
  name: string;
  code: string;
  code_color: string;
}

const HEX = /^#[0-9A-Fa-f]{6}$/;
const norm = (s: string) =>
  String(s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
/** "Retour en cours" → "RETOUR_EN_COURS" */
const toCode = (s: string) =>
  String(s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 50);

/** Readable text color on a colored background */
function textOn(hex: string) {
  if (!HEX.test(hex || "")) return "#1C1917";
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);
  const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return lum > 0.6 ? "#1C1917" : "#FFFFFF";
}

function apiMessage(error: any, fallback: string) {
  return error?.response?.data?.message || fallback;
}

export default defineComponent({
  name: "StatuSettings",
  components: { SettingsNav },
  setup() {
    const { showToast } = useAppToast();

    const statuts = ref<Statut[]>([]);
    const original = ref<Record<number, string>>({});
    const loading = ref(true);
    const loadError = ref(false);
    const search = ref("");
    const lastAddedId = ref<number | null>(null);

    const swatches = ["#FF5500", "#F59E0B", "#1D9A67", "#3B82F6", "#8B5CF6", "#E44A4A", "#0EA5E9", "#78716C"];

    async function fetchStatus() {
      loading.value = true;
      loadError.value = false;
      try {
        const response = await api.get("/api/auth/status_list");
        statuts.value = response.data || [];
        original.value = Object.fromEntries(statuts.value.map((s) => [s.id, s.code_color]));
      } catch (error) {
        console.error("Error fetching statuses:", error);
        loadError.value = true;
      } finally {
        loading.value = false;
      }
    }
    onMounted(fetchStatus);

    const filtered = computed(() => {
      const q = norm(search.value.trim());
      return q ? statuts.value.filter((s) => norm(s.name).includes(q) || norm(s.code).includes(q)) : statuts.value;
    });

    const pillStyle = (hex: string) => {
      const bg = HEX.test(hex || "") ? hex : "#E7E5E4";
      return { background: bg, color: textOn(bg) };
    };

    // ── Create ──
    const newStatut = reactive({ name: "", code: "", code_color: "#FF5500" });
    const errors = reactive<Record<string, string>>({});
    const saving = ref(false);
    let codeTouched = false;

    function onNameInput() {
      if (!codeTouched) newStatut.code = toCode(newStatut.name);
      delete errors.name;
    }
    function onCodeInput() {
      codeTouched = newStatut.code !== "";
      newStatut.code = newStatut.code.toUpperCase().replace(/[^A-Z0-9_]/g, "_");
      delete errors.code;
    }
    function validateNew() {
      Object.keys(errors).forEach((k) => delete errors[k]);
      if (!newStatut.name.trim()) errors.name = "Le nom est obligatoire.";
      if (!newStatut.code) errors.code = "Le code est obligatoire.";
      else if (!/^[A-Z0-9_]+$/.test(newStatut.code)) errors.code = "MAJUSCULES, chiffres et _ uniquement.";
      else if (statuts.value.some((s) => s.code === newStatut.code)) errors.code = "Ce code existe déjà.";
      if (newStatut.code_color && !HEX.test(newStatut.code_color)) errors.code_color = "Couleur invalide (ex. #FF5500).";
      return !Object.keys(errors).length;
    }

    async function submitForm() {
      if (saving.value || !validateNew()) return;
      saving.value = true;
      try {
        const formData = new FormData();
        formData.append("name", newStatut.name.trim());
        formData.append("code", newStatut.code);
        formData.append("code_color", newStatut.code_color);
        const response = await api.post("/api/auth/statut_add", formData);
        if (response.data?.success || response.data?.message === "success") {
          lastAddedId.value = response.data?.data?.id ?? null;
          await fetchStatus();
          newStatut.name = "";
          newStatut.code = "";
          newStatut.code_color = "#FF5500";
          codeTouched = false;
          showToast("Statut ajouté.", "success");
        } else {
          showToast(response.data?.message || "L'ajout a échoué.", "error");
        }
      } catch (error: any) {
        const errs = error?.response?.data?.errors || {};
        Object.keys(errs).forEach((k) => (errors[k] = errs[k][0]));
        showToast(apiMessage(error, "Une erreur est survenue lors de l'ajout."), "error");
      } finally {
        saving.value = false;
      }
    }

    // ── Inline color ──
    const savingId = ref<number | null>(null);
    function onColorChange(id: number, color: string) {
      const statut = statuts.value.find((s) => s.id === id);
      if (statut) statut.code_color = color;
    }
    const isColorDirty = (item: Statut) => (original.value[item.id] || "").toLowerCase() !== (item.code_color || "").toLowerCase();

    async function updateColor(statut: Statut) {
      savingId.value = statut.id;
      try {
        const res = await api.post(`/api/auth/statut_edit/${statut.id}`, { code_color: statut.code_color });
        if (res.data?.success || res.data?.message === "success") {
          original.value[statut.id] = statut.code_color;
          showToast("Couleur enregistrée.", "success");
        } else {
          showToast(res.data?.message ?? "Erreur lors de la mise à jour de la couleur.", "error");
        }
      } catch (error) {
        showToast(apiMessage(error, "Erreur lors de la mise à jour de la couleur."), "error");
      } finally {
        savingId.value = null;
      }
    }

    // ── Edit modal (name + color) ──
    const editModalEl = ref<HTMLElement | null>(null);
    let modal: Modal | null = null;
    const edit = reactive({ id: 0, name: "", code: "", code_color: "#FFFFFF" });
    const editErrors = reactive<Record<string, string>>({});
    const savingEdit = ref(false);

    function openEdit(item: Statut) {
      Object.assign(edit, { id: item.id, name: item.name, code: item.code, code_color: item.code_color || "#FFFFFF" });
      Object.keys(editErrors).forEach((k) => delete editErrors[k]);
      if (!modal && editModalEl.value) modal = new Modal(editModalEl.value);
      modal?.show();
    }
    async function saveEdit() {
      Object.keys(editErrors).forEach((k) => delete editErrors[k]);
      if (!edit.name.trim()) editErrors.name = "Le nom est obligatoire.";
      if (!HEX.test(edit.code_color)) editErrors.code_color = "Couleur invalide (ex. #FF5500).";
      if (Object.keys(editErrors).length || savingEdit.value) return;
      savingEdit.value = true;
      try {
        const res = await api.post(`/api/auth/statut_edit/${edit.id}`, { name: edit.name.trim(), code_color: edit.code_color });
        if (res.data?.success || res.data?.message === "success") {
          modal?.hide();
          await fetchStatus();
          showToast("Statut modifié.", "success");
        } else {
          showToast(res.data?.message ?? "La modification a échoué.", "error");
        }
      } catch (error: any) {
        const errs = error?.response?.data?.errors || {};
        Object.keys(errs).forEach((k) => (editErrors[k] = errs[k][0]));
        showToast(apiMessage(error, "La modification a échoué."), "error");
      } finally {
        savingEdit.value = false;
      }
    }
    onBeforeUnmount(() => modal?.dispose());

    return {
      statuts,
      loading,
      loadError,
      search,
      filtered,
      lastAddedId,
      swatches,
      pillStyle,
      fetchStatus,
      newStatut,
      errors,
      saving,
      onNameInput,
      onCodeInput,
      submitForm,
      savingId,
      onColorChange,
      isColorDirty,
      updateColor,
      editModalEl,
      edit,
      editErrors,
      savingEdit,
      openEdit,
      saveEdit,
    };
  },
});
</script>

<style scoped>
.sx-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}
.st-pill {
  display: inline-flex;
  align-items: center;
  max-width: 220px;
  padding: 5px 12px;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: var(--ui-radius-pill);
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-heavy);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.st-dot {
  position: relative;
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  cursor: pointer;
}
.st-dot input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}
.st-dot span {
  display: block;
  width: 100%;
  height: 100%;
  border: 3px solid var(--ui-surface);
  border-radius: 50%;
  box-shadow: 0 0 0 1px var(--ui-border-strong);
  transition: transform var(--ui-dur-fast) var(--ui-ease);
}
.st-dot:hover span,
.st-dot:focus-within span {
  transform: scale(1.1);
  box-shadow: 0 0 0 2px var(--ui-accent);
}
@media (max-width: 767.98px) {
  .sx-row .st-pill {
    display: none;
  }
}
</style>
